import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Send,
  Download,
  Eye,
  Receipt,
  Search,
  Filter,
  X
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const MerchantInvoicesPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  const [formData, setFormData] = useState({
    invoice_number: "",
    customer_name: "",
    customer_email: "",
    customer_address: "",
    customer_phone: "",
    company_name: "",
    company_address: "",
    company_phone: "",
    company_email: "",
    vat_number: "",
    items: [{ description: "", quantity: 1, unit_price: 0, amount: 0 }],
    tax_rate: 20,
    discount: 0,
    issue_date: new Date().toISOString().split("T")[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    payment_terms: "Net 30",
    bank_details: "",
    payment_instructions: "",
    notes: "",
    terms_conditions: ""
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API}/merchant/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items, taxRate, discount) => {
    const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount - discount;
    return { subtotal, taxAmount, total };
  };

  const updateItemAmount = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === "quantity" || field === "unit_price") {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    
    const { subtotal, taxAmount, total } = calculateTotals(newItems, formData.tax_rate, formData.discount);
    setFormData({ 
      ...formData, 
      items: newItems,
      subtotal,
      tax_amount: taxAmount,
      total
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const { subtotal, taxAmount, total } = calculateTotals(newItems, formData.tax_rate, formData.discount);
      setFormData({ 
        ...formData, 
        items: newItems,
        subtotal,
        tax_amount: taxAmount,
        total
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { subtotal, taxAmount, total } = calculateTotals(formData.items, formData.tax_rate, formData.discount);
      
      const invoiceData = {
        ...formData,
        subtotal,
        tax_amount: taxAmount,
        total,
        // Include merchant logo in invoice
        company_logo: user?.logo_url || ""
      };

      const url = editingInvoice 
        ? `${API}/merchant/invoices/${editingInvoice.invoice_id}`
        : `${API}/merchant/invoices`;
      
      const response = await fetch(url, {
        method: editingInvoice ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        toast.success(editingInvoice ? "Invoice updated!" : "Invoice created!");
        setShowCreateModal(false);
        setEditingInvoice(null);
        resetForm();
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save invoice");
      }
    } catch (error) {
      toast.error("Failed to save invoice");
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (!invoice.customer_email) {
      toast.error("Customer email is required to send invoice");
      return;
    }

    try {
      const response = await fetch(`${API}/merchant/invoices/${invoice.invoice_id}/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Invoice sent successfully!");
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invoice");
      }
    } catch (error) {
      toast.error("Failed to send invoice");
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await fetch(`${API}/merchant/invoices/${invoice.invoice_id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoice.invoice_number || invoice.invoice_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("PDF downloaded!");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm("Are you sure you want to delete this invoice?")) return;

    try {
      const response = await fetch(`${API}/merchant/invoices/${invoiceId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Invoice deleted!");
        fetchInvoices();
      } else {
        toast.error("Failed to delete invoice");
      }
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_number: "",
      customer_name: "",
      customer_email: "",
      customer_address: "",
      customer_phone: "",
      company_name: user?.business_name || "",
      company_address: "",
      company_phone: "",
      company_email: user?.email || "",
      vat_number: "",
      items: [{ description: "", quantity: 1, unit_price: 0, amount: 0 }],
      tax_rate: 20,
      discount: 0,
      issue_date: new Date().toISOString().split("T")[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      payment_terms: "Net 30",
      bank_details: "",
      payment_instructions: "",
      notes: "",
      terms_conditions: ""
    });
  };

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      invoice_number: invoice.invoice_number || "",
      customer_name: invoice.customer_name || "",
      customer_email: invoice.customer_email || "",
      customer_address: invoice.customer_address || "",
      customer_phone: invoice.customer_phone || "",
      company_name: invoice.company_name || "",
      company_address: invoice.company_address || "",
      company_phone: invoice.company_phone || "",
      company_email: invoice.company_email || "",
      vat_number: invoice.vat_number || "",
      items: invoice.items || [{ description: "", quantity: 1, unit_price: 0, amount: 0 }],
      tax_rate: invoice.tax_rate || 20,
      discount: invoice.discount || 0,
      issue_date: invoice.issue_date || "",
      due_date: invoice.due_date || "",
      payment_terms: invoice.payment_terms || "Net 30",
      bank_details: invoice.bank_details || "",
      payment_instructions: invoice.payment_instructions || "",
      notes: invoice.notes || "",
      terms_conditions: invoice.terms_conditions || ""
    });
    setShowCreateModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      sent: "bg-blue-100 text-blue-700",
      paid: "bg-emerald-100 text-emerald-700",
      overdue: "bg-red-100 text-red-700",
      cancelled: "bg-gray-100 text-gray-700"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = 
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto" />
              <div className="h-6 w-px bg-slate-300" />
              <span className="font-brand text-sm font-bold text-slate-900">VEYRONIX</span>
              <h1 className="text-xl font-bold text-slate-900">Invoices</h1>
            </div>
          </div>
          <Button 
            onClick={() => { resetForm(); setEditingInvoice(null); setShowCreateModal(true); }}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-invoice-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No invoices yet</h3>
            <p className="text-slate-500 mb-6">Create your first invoice to get started</p>
            <Button 
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Invoice #</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Customer</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Due Date</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.invoice_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{invoice.invoice_number}</td>
                    <td className="px-6 py-4">
                      <div className="text-slate-900">{invoice.customer_name}</div>
                      <div className="text-slate-500 text-sm">{invoice.customer_email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{invoice.issue_date}</td>
                    <td className="px-6 py-4 text-slate-500">{invoice.due_date}</td>
                    <td className="px-6 py-4 text-right font-semibold text-slate-900">£{(invoice.total || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-center">{getStatusBadge(invoice.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setViewingInvoice(invoice)}
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadPdf(invoice)}
                          title="Download PDF"
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {invoice.status !== "paid" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditModal(invoice)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSendInvoice(invoice)}
                              title="Send"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteInvoice(invoice.invoice_id)}
                          title="Delete"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create/Edit Invoice Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? "Edit Invoice" : "Create New Invoice"}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Invoice Details */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="INV-001"
                />
              </div>
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
            </div>

            {/* Customer Details */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Customer Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Customer Email *</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Customer Phone</Label>
                  <Input
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Customer Address</Label>
                  <Input
                    value={formData.customer_address}
                    onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Company Details */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Your Business Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>VAT Number</Label>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    placeholder="GB123456789"
                  />
                </div>
                <div>
                  <Label>Company Email</Label>
                  <Input
                    type="email"
                    value={formData.company_email}
                    onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Company Phone</Label>
                  <Input
                    value={formData.company_phone}
                    onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Company Address</Label>
                  <Textarea
                    value={formData.company_address}
                    onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Line Items</h3>
                <Button type="button" size="sm" variant="outline" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-slate-500">
                  <div className="col-span-5">Description</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2">Unit Price</div>
                  <div className="col-span-2">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2">
                    <div className="col-span-5">
                      <Input
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => updateItemAmount(index, "description", e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItemAmount(index, "quantity", parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItemAmount(index, "unit_price", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        value={item.amount.toFixed(2)}
                        readOnly
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="col-span-1">
                      {formData.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(index)}>
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border-t pt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>£{calculateTotals(formData.items, formData.tax_rate, formData.discount).subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span>VAT (</span>
                      <Input
                        type="number"
                        className="w-16 h-6 text-xs"
                        value={formData.tax_rate}
                        onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                      />
                      <span>%):</span>
                    </div>
                    <span>£{calculateTotals(formData.items, formData.tax_rate, formData.discount).taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Discount:</span>
                    <div className="flex items-center gap-1">
                      <span>-£</span>
                      <Input
                        type="number"
                        className="w-20 h-6 text-xs"
                        value={formData.discount}
                        onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total:</span>
                    <span>£{calculateTotals(formData.items, formData.tax_rate, formData.discount).total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Payment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Terms</Label>
                  <Select
                    value={formData.payment_terms}
                    onValueChange={(value) => setFormData({ ...formData, payment_terms: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bank Details</Label>
                  <Textarea
                    value={formData.bank_details}
                    onChange={(e) => setFormData({ ...formData, bank_details: e.target.value })}
                    placeholder="Account Name: ...\nSort Code: ...\nAccount Number: ..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Payment Instructions</Label>
                  <Textarea
                    value={formData.payment_instructions}
                    onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                    placeholder="How to pay this invoice..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                {editingInvoice ? "Update Invoice" : "Create Invoice"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={!!viewingInvoice} onOpenChange={() => setViewingInvoice(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {viewingInvoice && (
            <div className="p-6">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-indigo-600">INVOICE</h1>
                  <p className="text-slate-500">{viewingInvoice.invoice_number}</p>
                </div>
                <div className="text-right">
                  <h3 className="font-bold">{viewingInvoice.company_name}</h3>
                  <p className="text-sm text-slate-500">{viewingInvoice.company_address}</p>
                  <p className="text-sm text-slate-500">{viewingInvoice.company_email}</p>
                  {viewingInvoice.vat_number && <p className="text-sm text-slate-500">VAT: {viewingInvoice.vat_number}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-8 p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Bill To:</h4>
                  <p className="font-semibold">{viewingInvoice.customer_name}</p>
                  <p className="text-sm text-slate-500">{viewingInvoice.customer_address}</p>
                  <p className="text-sm text-slate-500">{viewingInvoice.customer_email}</p>
                </div>
                <div className="text-right">
                  <p><strong>Issue Date:</strong> {viewingInvoice.issue_date}</p>
                  <p><strong>Due Date:</strong> {viewingInvoice.due_date}</p>
                  <p><strong>Status:</strong> {getStatusBadge(viewingInvoice.status)}</p>
                </div>
              </div>

              <table className="w-full mb-8">
                <thead>
                  <tr className="bg-indigo-600 text-white">
                    <th className="text-left p-3">Description</th>
                    <th className="text-center p-3">Qty</th>
                    <th className="text-right p-3">Unit Price</th>
                    <th className="text-right p-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingInvoice.items?.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3">{item.description}</td>
                      <td className="text-center p-3">{item.quantity}</td>
                      <td className="text-right p-3">£{(item.unit_price || 0).toFixed(2)}</td>
                      <td className="text-right p-3">£{(item.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-end mb-8">
                <div className="w-64">
                  <div className="flex justify-between py-2">
                    <span>Subtotal:</span>
                    <span>£{(viewingInvoice.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>VAT ({viewingInvoice.tax_rate || 20}%):</span>
                    <span>£{(viewingInvoice.tax_amount || 0).toFixed(2)}</span>
                  </div>
                  {viewingInvoice.discount > 0 && (
                    <div className="flex justify-between py-2">
                      <span>Discount:</span>
                      <span>-£{(viewingInvoice.discount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-t font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-indigo-600">£{(viewingInvoice.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {viewingInvoice.bank_details && (
                <div className="p-4 bg-slate-50 rounded-lg mb-4">
                  <h4 className="font-semibold mb-2">Bank Details:</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{viewingInvoice.bank_details}</p>
                </div>
              )}

              {viewingInvoice.notes && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Notes:</h4>
                  <p className="text-sm text-slate-600">{viewingInvoice.notes}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button onClick={() => handleDownloadPdf(viewingInvoice)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
                {viewingInvoice.status !== "paid" && (
                  <Button onClick={() => handleSendInvoice(viewingInvoice)} className="bg-blue-600 hover:bg-blue-700">
                    <Send className="h-4 w-4 mr-2" /> Send to Customer
                  </Button>
                )}
                <Button variant="outline" onClick={() => setViewingInvoice(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantInvoicesPage;
